import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/content";
import { scrollToId } from "@/hooks/useLenis";
import { BrandImg } from "@/components/site/Brand";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          scrolled ? "bg-rc-bg/70 backdrop-blur-xl border-rc-line" : "bg-transparent border-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <button onClick={() => go("top")} data-testid="nav-logo" aria-label="The Rhett Co home">
            <BrandImg alt="The Rhett Co" className="h-7 w-auto sm:h-8" />
          </button>
          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                data-testid={`nav-link-${n.id}`}
                className="group relative font-mono-rc text-xs uppercase tracking-[0.22em] text-rc-body transition-colors hover:text-rc-ink"
              >
                {n.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-rc-copper transition-transform duration-500 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => go("inquire")}
              data-testid="nav-inquire-btn"
              className="group hidden items-center gap-2 rounded-full bg-rc-copper px-5 py-2.5 text-sm font-medium text-rc-bg transition-[background-color,transform] duration-300 hover:bg-rc-glow active:scale-95 sm:flex"
            >
              Inquire
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button
              onClick={() => setOpen(true)}
              className="grid h-11 w-11 place-items-center rounded-full border border-rc-line text-rc-ink lg:hidden"
              data-testid="nav-menu-open"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "circle(0% at 92% 4%)" }}
            animate={{ clipPath: "circle(150% at 92% 4%)" }}
            exit={{ clipPath: "circle(0% at 92% 4%)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[70] flex flex-col bg-rc-bg px-6 pb-10 pt-6"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <BrandImg alt="The Rhett Co" className="h-7" />
              <button onClick={() => setOpen(false)} className="grid h-11 w-11 place-items-center rounded-full border border-rc-line" data-testid="nav-menu-close" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-16 flex flex-col gap-2">
              {[...NAV, { id: "inquire", label: "Inquire" }].map((n, i) => (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.06 }}
                  onClick={() => go(n.id)}
                  className="text-left font-display text-5xl text-rc-ink hover:text-rc-copper"
                  data-testid={`mobile-nav-${n.id}`}
                >
                  {n.label}
                </motion.button>
              ))}
            </div>
            <p className="mt-auto font-mono-rc text-xs uppercase tracking-[0.22em] text-rc-muted">Charlotte, NC · admin@rhettco.org</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
